import { Link } from 'react-router-dom';
import { useCart } from '@/context/CartContext';
import { priceFor, inr, imgUrl } from '@/lib/api';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Trash2, Minus, Plus, ShoppingBag, ArrowRight } from 'lucide-react';

export default function Cart() {
    const { items, updateQty, updateQuality, removeItem, subtotal } = useCart();

    if (items.length === 0) {
        return (
            <div className="kanbr-container py-16 text-center">
                <ShoppingBag className="w-14 h-14 mx-auto text-slate-300 mb-4" />
                <h1 className="font-display text-3xl font-bold text-slate-900">Your cart is empty</h1>
                <p className="text-slate-600 mt-3 text-sm">Browse our pre-crafted modules to start building.</p>
                <Link to="/pre-crafted-modules" className="btn-primary rounded-none mt-6 inline-flex" data-testid="cart-browse">
                    Browse modules <ArrowRight className="w-4 h-4" />
                </Link>
            </div>
        );
    }

    return (
        <div className="kanbr-container py-10">
            <div className="eyebrow mb-2">Your cart</div>
            <h1 className="font-display text-3xl md:text-4xl font-bold text-slate-900 mb-8">Review your selection.</h1>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 space-y-3" data-testid="cart-items">
                    {items.map((it) => {
                        const price = priceFor(it.product, it.quality);
                        return (
                            <div key={`${it.product.id}::${it.quality}`} className="border border-slate-200 bg-white p-4 flex gap-4" data-testid={`cart-item-${it.product.sku}`}>
                                <img src={imgUrl(it.product.image_url)} alt={it.product.name} className="w-24 h-24 object-contain product-img-bg p-2 flex-shrink-0" />
                                <div className="flex-1 flex flex-col">
                                    <div className="flex justify-between items-start gap-3">
                                        <div>
                                            <h3 className="font-display font-semibold text-slate-900 text-base leading-tight">{it.product.name}</h3>
                                            <div className="text-[11px] text-slate-500 mt-1">{it.product.sku} · {it.product.size}</div>
                                        </div>
                                        <button onClick={() => removeItem(it.product.id, it.quality)} data-testid={`cart-remove-${it.product.sku}`} className="text-slate-400 hover:text-red-600 p-1"><Trash2 className="w-4 h-4" /></button>
                                    </div>

                                    <div className="mt-auto flex items-end justify-between gap-3 flex-wrap pt-3">
                                        <div className="flex items-end gap-3">
                                            <div>
                                                <div className="eyebrow mb-1">Quality</div>
                                                <Select value={it.quality} onValueChange={(v) => updateQuality(it.product.id, it.quality, v)}>
                                                    <SelectTrigger className="w-32 h-8 rounded-none text-xs" data-testid={`cart-quality-${it.product.sku}`}>
                                                        <SelectValue />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        <SelectItem value="eco">Eco</SelectItem>
                                                        <SelectItem value="prime">Prime</SelectItem>
                                                        <SelectItem value="gold">Gold</SelectItem>
                                                    </SelectContent>
                                                </Select>
                                            </div>
                                            <div className="flex items-center border border-slate-300">
                                                <button onClick={() => updateQty(it.product.id, it.quality, it.quantity - 1)} className="w-8 h-8 hover:bg-slate-100 flex items-center justify-center" data-testid={`cart-qty-minus-${it.product.sku}`}><Minus className="w-3 h-3" /></button>
                                                <span className="w-8 text-center text-sm font-semibold">{it.quantity}</span>
                                                <button onClick={() => updateQty(it.product.id, it.quality, it.quantity + 1)} className="w-8 h-8 hover:bg-slate-100 flex items-center justify-center" data-testid={`cart-qty-plus-${it.product.sku}`}><Plus className="w-3 h-3" /></button>
                                            </div>
                                        </div>
                                        <div className="text-right">
                                            <div className="text-[11px] text-slate-500">{inr(price)} × {it.quantity}</div>
                                            <div className="font-display text-lg font-bold text-slate-900">{inr(price * it.quantity)}</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>

                <aside className="border border-slate-200 bg-white p-6 h-fit lg:sticky lg:top-24" data-testid="cart-summary">
                    <h2 className="font-display text-xl font-bold text-slate-900 mb-4">Order summary</h2>
                    {(() => {
                        const gst = Math.round(subtotal * 0.18);
                        const grand = subtotal + gst;
                        return (
                            <>
                                <div className="space-y-2 text-sm text-slate-700">
                                    <div className="flex justify-between"><span>Subtotal</span><span data-testid="cart-subtotal">{inr(subtotal)}</span></div>
                                    <div className="flex justify-between"><span>GST (18%)</span><span data-testid="cart-gst">{inr(gst)}</span></div>
                                    <div className="flex justify-between text-xs text-slate-500"><span>Shipping</span><span>Calculated at checkout</span></div>
                                </div>
                                <div className="border-t border-slate-200 my-4" />
                                <div className="flex justify-between text-base font-display font-bold text-slate-900">
                                    <span>Final amount</span><span data-testid="cart-final">{inr(grand)}</span>
                                </div>
                                <div className="text-[11px] text-slate-500 mt-2">Confirm order with 10% advance at checkout.</div>
                            </>
                        );
                    })()}
                    <Link to="/checkout" className="btn-accent rounded-none w-full justify-center mt-5" data-testid="cart-checkout">
                        Proceed to Checkout <ArrowRight className="w-4 h-4" />
                    </Link>
                </aside>
            </div>
        </div>
    );
}
